import Box from '@mui/material/Box';
import { alpha, keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { FC } from 'react';

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(0.85); opacity: 0.5; }
  50% { transform: scale(1); opacity: 1; }
`;

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0.4); opacity: 0.3; }
  40% { transform: scale(1); opacity: 1; }
`;

export type EmailChangeLoaderProps = {
  title: string;
  subtitle?: string;
};

/** Animated loader shown while an email-change link is being processed. */
export const EmailChangeLoader: FC<EmailChangeLoaderProps> = ({ title, subtitle }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        py: 6,
        px: 3,
        maxWidth: 420,
        mx: 'auto',
        animation: `${fadeIn} 0.4s ease-out`,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 88,
          height: 88,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Rotating gradient arc */}
        <Box
          sx={theme => ({
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: `conic-gradient(from 0deg, ${alpha(theme.palette.primary.main, 0)} 0deg, ${theme.palette.primary.main} 300deg, ${alpha(theme.palette.primary.main, 0)} 360deg)`,
            WebkitMask: 'radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px))',
            mask: 'radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px))',
            animation: `${spin} 1.1s linear infinite`,
          })}
        />

        {/* Pulsing core */}
        <Box
          sx={theme => ({
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            boxShadow: `0 0 0 8px ${alpha(theme.palette.primary.main, 0.08)}`,
            animation: `${pulse} 1.6s ease-in-out infinite`,
          })}
        />
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {[0, 1, 2].map(i => (
          <Box
            key={i}
            sx={theme => ({
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: theme.palette.primary.main,
              animation: `${bounce} 1.2s ease-in-out infinite`,
              animationDelay: `${i * 0.15}s`,
            })}
          />
        ))}
      </Box>
    </Box>
  );
};
